import { useCallback, useEffect, useState } from "react";

import { authFetch } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { Pill, statusToTone } from "@/components/Pill";
import { useStore } from "@/state/store";

interface Market {
  id: string;
  slug: string;
  title: string;
  status: string;
  close_at: string;
  resolve_after: string;
  resolution_source: string;
  description?: string;
  outcomes?: Outcome[];
}

interface MarketListResponse {
  items: Market[];
  next_cursor?: string | null;
}

interface Outcome {
  id: string;
  label: string;
  position?: number;
}

interface ResolutionAttempt {
  id: string;
  attempt_no?: number;
  attempted_at?: string;
  status: string;
  detail?: string | null;
}

interface ResolutionStatusView {
  market_id: string;
  market_status: string;
  resolution_source: string;
  winning_outcome_id: string | null;
  attempts: ResolutionAttempt[];
  dispute_window_ends_at: string | null;
}

type StatusFilter = "closed" | "all";

/**
 * Pagina di monitoraggio risoluzioni.
 *
 * Default: mostra solo i mercati in stato `closed` che attendono outcome.
 * Toggle per estendere a tutti gli stati (utile per audit).
 *
 * Azione principale: "Set manual outcome" — apre il modal con i possibili
 * outcomes e raccoglie evidence_url + evidence_note. Endpoint:
 *   POST /v1/admin/markets/:slug/proposed-outcome
 *
 * Per admin è disponibile anche il fallback emergency: "Forza risoluzione"
 * (ADR-007). Bypass UMA — da usare solo in caso di malfunzionamento oracolo.
 */
export function ResolutionsPage() {
  const auth = useAuth();
  const showToast = useStore((s) => s.showToast);

  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [filter, setFilter] = useState<StatusFilter>("closed");
  const [proposing, setProposing] = useState<Market | null>(null);
  const [forcing, setForcing] = useState<Market | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const url =
        filter === "closed"
          ? "/v1/markets?status=closed&limit=50"
          : "/v1/markets?limit=50";
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`markets ${res.status}`);
      const data = (await res.json()) as MarketListResponse;
      setMarkets(data.items ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "errore sconosciuto");
      setMarkets([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: DataTableColumn<Market>[] = [
    {
      key: "slug",
      header: "Market",
      cell: (m) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-text text-[13px]">{m.title}</span>
          <span className="font-mono text-[11px] text-muted">{m.slug}</span>
        </div>
      ),
      sortValue: (m) => m.slug,
    },
    {
      key: "close_at",
      header: "Closed at",
      cell: (m) => (
        <span className="font-mono text-[11.5px] text-text-2">
          {formatDate(m.close_at)}
        </span>
      ),
      sortValue: (m) => new Date(m.close_at),
    },
    {
      key: "resolve_after",
      header: "Resolve after",
      cell: (m) => (
        <span className="font-mono text-[11.5px] text-muted">
          {formatDate(m.resolve_after)}
        </span>
      ),
      sortValue: (m) => new Date(m.resolve_after),
    },
    {
      key: "source",
      header: "Oracolo",
      cell: (m) => (
        <span className="font-mono text-[11.5px] text-text-2">
          {m.resolution_source}
        </span>
      ),
      sortValue: (m) => m.resolution_source,
    },
    {
      key: "status",
      header: "Status",
      cell: (m) => <Pill tone={statusToTone(m.status)}>{m.status}</Pill>,
      sortValue: (m) => m.status,
    },
    {
      key: "resolution_status",
      header: "Resolution",
      cell: (m) => <ResolutionStatusCell slug={m.slug} />,
    },
    {
      key: "actions",
      header: "Azioni",
      align: "right",
      cell: (m) => (
        <div className="inline-flex gap-2 justify-end">
          <button
            type="button"
            className="btn-secondary py-1 px-3 text-[11.5px]"
            onClick={(e) => {
              e.stopPropagation();
              setProposing(m);
            }}
          >
            Proponi outcome
          </button>
          {auth.role === "admin" && (
            <button
              type="button"
              className="btn-secondary py-1 px-3 text-[11.5px] border-no/40 text-no hover:bg-no/10"
              onClick={(e) => {
                e.stopPropagation();
                setForcing(m);
              }}
              title="Bypass UMA (emergency)"
            >
              Forza
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4 max-w-[1400px]">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold">Risoluzioni</h1>
          <p className="text-[13px] text-muted mt-1">
            Mercati chiusi in attesa di outcome. Proponi una resolution manuale
            o monitora gli attempt UMA in corso.
          </p>
        </div>
        <div className="card py-2 px-3 text-[12px]">
          <span className="text-muted">Pending </span>
          <span className="font-mono text-accent">{markets.length}</span>
          <span className="text-muted"> markets</span>
        </div>
      </header>

      <div className="flex items-center gap-2">
        <span className="text-[12px] text-muted">Filtro:</span>
        <button
          type="button"
          className={`px-3 py-1 rounded-pill text-[11.5px] border transition-colors ${
            filter === "closed"
              ? "border-accent/40 text-accent bg-accent/10"
              : "border-line text-muted hover:text-text-2"
          }`}
          onClick={() => setFilter("closed")}
        >
          solo closed
        </button>
        <button
          type="button"
          className={`px-3 py-1 rounded-pill text-[11.5px] border transition-colors ${
            filter === "all"
              ? "border-accent/40 text-accent bg-accent/10"
              : "border-line text-muted hover:text-text-2"
          }`}
          onClick={() => setFilter("all")}
        >
          tutti
        </button>
      </div>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento markets fallito: {err}
        </div>
      )}

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : (
        <DataTable
          rows={markets}
          columns={columns}
          rowKey={(m) => m.id}
          pageSize={25}
          empty={
            <div className="px-4 py-8 text-center text-muted text-[13px]">
              {filter === "closed"
                ? "Nessun market in attesa di risoluzione."
                : "Nessun market trovato."}
            </div>
          }
        />
      )}

      {proposing && (
        <ProposeOutcomeModal
          market={proposing}
          onClose={() => setProposing(null)}
          onDone={() => {
            setProposing(null);
            showToast({
              kind: "success",
              message: `Outcome proposto per ${proposing.slug}.`,
            });
            load();
          }}
        />
      )}

      {forcing && (
        <ForceResolveModal
          market={forcing}
          onClose={() => setForcing(null)}
          onDone={() => {
            setForcing(null);
            showToast({
              kind: "success",
              message: `Risoluzione forzata per ${forcing.slug}.`,
            });
            load();
          }}
        />
      )}
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ───────────────── Resolution status (lazy per row) ───────────────── */

/** Fetch lazy dello stato di resolution per un singolo market. Il risultato
 *  ha una tonalità di pill compatta in tabella. */
function ResolutionStatusCell({ slug }: { slug: string }) {
  const [status, setStatus] = useState<ResolutionStatusView | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await authFetch(
          `/v1/markets/${encodeURIComponent(slug)}/resolution`,
        );
        if (!res.ok) {
          if (res.status === 404) {
            // Market senza attempt registrati: stato "idle" implicito.
            if (!cancelled) {
              setStatus(null);
              setErr(null);
            }
            return;
          }
          throw new Error(`${res.status}`);
        }
        const data = (await res.json()) as ResolutionStatusView;
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "err");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return <span className="text-[11px] text-muted">…</span>;
  }
  if (err) {
    return <Pill tone="no">err</Pill>;
  }
  if (!status || status.attempts.length === 0) {
    return <Pill tone="neutral">idle</Pill>;
  }
  const last = status.attempts[status.attempts.length - 1];
  const tone = attemptTone(last.status);
  return (
    <Pill tone={tone} className="font-mono">
      {last.status}
    </Pill>
  );
}

function attemptTone(
  status: string,
): "neutral" | "yes" | "no" | "accent" | "warn" {
  switch (status) {
    case "finalized":
    case "settled":
      return "yes";
    case "failed":
    case "disputed":
      return "no";
    case "proposed":
      return "accent";
    case "proposing":
    case "pending":
      return "warn";
    default:
      return "neutral";
  }
}

/* ───────────────── Propose outcome modal ───────────────── */

function ProposeOutcomeModal({
  market,
  onClose,
  onDone,
}: {
  market: Market;
  onClose: () => void;
  onDone: () => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [outcomes, setOutcomes] = useState<Outcome[]>(market.outcomes ?? []);
  const [outcomeId, setOutcomeId] = useState<string>("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [evidenceNote, setEvidenceNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingOutcomes, setLoadingOutcomes] = useState(false);

  // Se la lista mercati non include gli outcomes inline, li carichiamo
  // dall'endpoint dedicato.
  useEffect(() => {
    if (outcomes.length > 0) return;
    let cancelled = false;
    async function load() {
      setLoadingOutcomes(true);
      try {
        const res = await authFetch(
          `/v1/markets/${encodeURIComponent(market.slug)}`,
        );
        if (!res.ok) throw new Error(`${res.status}`);
        const data = (await res.json()) as Market;
        if (!cancelled) {
          setOutcomes(data.outcomes ?? []);
          if (data.outcomes && data.outcomes[0]) {
            setOutcomeId(data.outcomes[0].id);
          }
        }
      } catch {
        /* ignora: la dropdown rimane vuota e il submit fallirà */
      } finally {
        if (!cancelled) setLoadingOutcomes(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [market.slug, outcomes.length]);

  // Default selection: primo outcome se non già impostato.
  useEffect(() => {
    if (!outcomeId && outcomes[0]) setOutcomeId(outcomes[0].id);
  }, [outcomes, outcomeId]);

  async function handleSubmit() {
    if (!outcomeId) {
      showToast({ kind: "error", message: "Seleziona un outcome." });
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, string> = { outcome_id: outcomeId };
      if (evidenceUrl.trim()) body.evidence_url = evidenceUrl.trim();
      if (evidenceNote.trim()) body.evidence_note = evidenceNote.trim();
      const res = await authFetch(
        `/v1/admin/markets/${encodeURIComponent(market.slug)}/proposed-outcome`,
        { method: "POST", body: JSON.stringify(body) },
      );
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const errBody = await res.json();
          detail = errBody.detail ?? errBody.message ?? null;
        } catch {
          /* ignore */
        }
        throw new Error(detail ?? `Proposta fallita (${res.status})`);
      }
      onDone();
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "errore",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[520px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Proponi outcome</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>

        <div className="mb-4">
          <div className="text-[15px] font-medium text-text">{market.title}</div>
          <div className="font-mono text-[11px] text-muted mt-1">
            {market.slug}
          </div>
          {market.description && (
            <p className="mt-2 text-[12.5px] text-text-2 leading-snug whitespace-pre-line">
              {market.description}
            </p>
          )}
        </div>

        <label className="flex flex-col gap-1 mb-3">
          <span className="text-[11.5px] text-muted">Outcome vincente</span>
          {loadingOutcomes ? (
            <div className="input text-muted">Caricamento outcomes…</div>
          ) : outcomes.length === 0 ? (
            <div className="input text-no">
              Nessun outcome configurato per questo market.
            </div>
          ) : (
            <select
              className="input"
              value={outcomeId}
              onChange={(e) => setOutcomeId(e.target.value)}
              disabled={submitting}
            >
              {outcomes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                  {typeof o.position === "number" ? ` (#${o.position})` : ""}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="flex flex-col gap-1 mb-3">
          <span className="text-[11.5px] text-muted">Evidence URL</span>
          <input
            className="input"
            type="url"
            placeholder="https://…"
            value={evidenceUrl}
            onChange={(e) => setEvidenceUrl(e.target.value)}
            disabled={submitting}
          />
        </label>

        <label className="flex flex-col gap-1 mb-4">
          <span className="text-[11.5px] text-muted">Note (opzionali)</span>
          <textarea
            className="input min-h-[80px] resize-y"
            placeholder="Contesto della proposta…"
            value={evidenceNote}
            onChange={(e) => setEvidenceNote(e.target.value)}
            disabled={submitting}
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !outcomeId}
          >
            {submitting ? "Invio…" : "Inserisci proposta"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Force resolve (admin / emergency) ───────────────── */

/**
 * Override emergenziale di una risoluzione (ADR-007). Bypassa UMA.
 *
 * UX deliberatamente attritivo: chiede di scrivere `CONFIRM` per evitare
 * azioni accidentali. Visibile solo agli admin.
 */
function ForceResolveModal({
  market,
  onClose,
  onDone,
}: {
  market: Market;
  onClose: () => void;
  onDone: () => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [outcomes, setOutcomes] = useState<Outcome[]>(market.outcomes ?? []);
  const [outcomeId, setOutcomeId] = useState<string>("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingOutcomes, setLoadingOutcomes] = useState(false);

  useEffect(() => {
    if (outcomes.length > 0) return;
    let cancelled = false;
    async function load() {
      setLoadingOutcomes(true);
      try {
        const res = await authFetch(
          `/v1/markets/${encodeURIComponent(market.slug)}`,
        );
        if (!res.ok) throw new Error(`${res.status}`);
        const data = (await res.json()) as Market;
        if (!cancelled) {
          setOutcomes(data.outcomes ?? []);
          if (data.outcomes && data.outcomes[0]) {
            setOutcomeId(data.outcomes[0].id);
          }
        }
      } catch {
        /* ignore: niente outcomes, submit bloccato */
      } finally {
        if (!cancelled) setLoadingOutcomes(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [market.slug, outcomes.length]);

  useEffect(() => {
    if (!outcomeId && outcomes[0]) setOutcomeId(outcomes[0].id);
  }, [outcomes, outcomeId]);

  async function handleForce() {
    if (!outcomeId || confirm !== "CONFIRM") return;
    setSubmitting(true);
    try {
      const res = await authFetch(
        `/v1/admin/markets/${encodeURIComponent(market.id)}/resolve/${encodeURIComponent(outcomeId)}`,
        { method: "POST" },
      );
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const body = await res.json();
          detail = body.detail ?? body.message ?? null;
        } catch {
          /* ignore */
        }
        throw new Error(detail ?? `Risoluzione fallita (${res.status})`);
      }
      onDone();
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "errore",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !!outcomeId && confirm === "CONFIRM" && !submitting;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[480px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold text-no">
            Forza risoluzione
          </h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <div className="mb-4 rounded-md border border-no/30 bg-no/10 px-3 py-2 text-[12px] text-no">
          Operazione di emergenza. Bypassa UMA e marca il market come risolto
          istantaneamente. Tracciata su audit log (ADR-007).
        </div>

        <div className="mb-3">
          <div className="text-[14px] font-medium text-text">{market.title}</div>
          <div className="font-mono text-[11px] text-muted mt-1">
            {market.slug}
          </div>
        </div>

        <label className="flex flex-col gap-1 mb-3">
          <span className="text-[11.5px] text-muted">Outcome vincente</span>
          {loadingOutcomes ? (
            <div className="input text-muted">Caricamento outcomes…</div>
          ) : outcomes.length === 0 ? (
            <div className="input text-no">Nessun outcome disponibile.</div>
          ) : (
            <select
              className="input"
              value={outcomeId}
              onChange={(e) => setOutcomeId(e.target.value)}
              disabled={submitting}
            >
              {outcomes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                  {typeof o.position === "number" ? ` (#${o.position})` : ""}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="flex flex-col gap-1 mb-4">
          <span className="text-[11.5px] text-muted">
            Per confermare, scrivi <span className="font-mono">CONFIRM</span>
          </span>
          <input
            className="input font-mono"
            placeholder="CONFIRM"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={handleForce}
            disabled={!canSubmit}
          >
            {submitting ? "Invio…" : "Forza risoluzione"}
          </button>
        </div>
      </div>
    </div>
  );
}
